import React, { useEffect, useState, useRef, ReactElement } from 'react';
import _ from 'lodash';
import styled from 'styled-components';
import { useParams } from 'react-router-dom';
import { Button, FilterSideBar, Search } from '@arcflight/tf-component-library';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import {
  changeDrawerContent,
  changeDrawerMode,
  changeDrawerVisibility,
  setDrawerId,
  DrawerMode,
} from '../../models/drawer';
import { usePrevious } from '../../utils/utils';
import RadioInputButton from '../../components/RadioInputButton/RadioInputButton';
import { getCabinSVG, setSelectedCabinIssueLocation } from '../../models/cabinIssues/action';
import InnerMenuLayoutNew from '../../layouts/InnerMenuLayoutNew';
import EmptyStateIntermittent from '../../assets/emptyState/empty-state-intermittent-faults.svg';
import { getSingleAircraft } from '../../models/aircraft/actions';
import { DashboardState } from '../../models';
import plusIcon from '../../assets/plus.svg';
import { ButtonSize } from '../../components/PaginatedDefectsTable/DefectTableHeader';
import { fetchImage } from '../../services/api';
import { CabinIssuesActionTypes } from '../../models/cabinIssues';
import EmptyState from '../../components/EmptyState/EmptyState';
import TFLoading from '../../components/TFLoading';
import redPin from './icon-pin-red.svg';
import amberPin from './icon-pin-amber.svg';
import CabinLogTable from './CabinLogTable';
import CabinIssueDrawer from './CabinIssueDrawer';
import cabinIssueFilterModel from './cabinIssueFilterModel';
import useQueryCabinIssues from './QueryCalls/useQueryCabinIssues';
import useQueryCabinIssueTotals from './QueryCalls/useQueryCabinIssueTotals';

interface CustomWindow extends Window {
  alterLocation?: (id: string, styleState: string) => void;
  selectLocation?: (id: string, symbolType: string) => void;
}

const Card = styled.div`
  background-color: #fafafa;
  border: 1px solid #ffffff;
  border-radius: 3px;
  width: 100%;
  padding: 24px;
  display: flex;
  justify-content: space-between;
  div,
  img,
  button,
  input {
    box-sizing: revert;
    line-height: normal;
  }
`;

const TableWrapper = styled.div`
  width: 100%;
  margin-right: 20px;
`;

const SVGContentWrapper = styled.div`
  display: flex;
`;

const SVGContent = styled.div`
  width: auto;
  position: relative;
  @media (max-width: 1450px) {
    display: none;
  }
`;

const SVGContentMobile = styled.div`
  width: 100%;
  display: none;
  position: relative;
  @media (max-width: 1450px) {
    position: relative;
    display: block;
  }
`;

const IframeWrapper = styled.div`
  position: relative;
`;

const PinOverlay = styled.div<{ clipWidth?: number; clipHeight?: number }>`
  position: absolute;
  top: 0;
  left: 0;
  width: ${(props) => (props.clipWidth ? `${props.clipWidth}px` : '100%')};
  height: ${(props) => (props.clipHeight ? `${props.clipHeight}px` : '100%')};
  overflow: hidden;
  pointer-events: none;

  button {
    pointer-events: auto;
  }
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  width: 100%;
  flex-wrap: wrap;
  gap: 16px;
`;

const PageTitle = styled.div`
  font-size: 16px;
  font-weight: 500;
  width: 150px;
`;

const TableActions = styled.div`
  display: flex;
`;

const SearchWrapper = styled.div`
  margin-right: 12px;
`;

const StyledButton = styled.button`
  border: none;
  background-color: transparent;
  cursor: pointer;
  padding: 0;
  margin: 0;
  &:focus {
    outline: none;
  }
`;

const RadioWrapper = styled.div`
  display: flex;
`;

const LocationText = styled.div`
  margin-top: 8px;
  margin-bottom: 20px;
`;

const LocationWrapper = styled.div`
  margin-left: 10px;
`;

const CabinLog: React.FC = () => {
  const {
    aircraft: { aircraftMap },
    cabinIssues: { cabinIssueSVG },
    userSettings: { details },
  } = useSelector((state: DashboardState) => state);

  const iframeRef = useRef(null);
  const locationRef = useRef(null);
  const cabinIssuesRef = useRef([]);

  const { id } = useParams<{ id: string }>();
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();

  const operatorSettings = details?.operators?.find(
    (op) => aircraftMap.get(id)?.operator_id === op?.id,
  )?.operator_setting;

  const cabinIssueOverride = operatorSettings?.cabin_issue_name_singular;
  const supportEmail = operatorSettings?.support_email;

  const mobileView = window.innerWidth < 451;
  const isCompactViewport = (): boolean => window.innerWidth < 1450;

  const [searchValue, setSearchValue] = useState('');
  const [reset, setReset] = useState(false);
  const [filters, setFilters] = useState(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [sortBy, setSortBy] = useState([]);
  const [cabinIssuesCount, setCabinIssuesCount] = useState(0);
  const [cabinIssues, setCabinIssues] = useState([]);
  const [cabinIssuesOriginal, setCabinIssuesOriginal] = useState([]);
  const [filterModel, setFilterModel] = useState(cabinIssueFilterModel());
  const [svgContent, setsvgContent] = useState(null);
  const [issuePoints, setIssuePoints] = useState([]);
  const [iframeWidth, setIframeWidth] = useState(null);
  const [iframeHeight, setIframeHeight] = useState(null);
  const [clipWidth, setClipWidth] = useState(null);
  const [clipHeight, setClipHeight] = useState(null);
  const [location, setLocation] = useState('location');
  const [isCompactView, setIsCompactView] = useState(isCompactViewport());

  const previousFilters = usePrevious(filters);
  const previousSearchValue = usePrevious(searchValue);

  const { data: cabinData, isFetching } = useQueryCabinIssues({
    aircraft_id: id,
    page,
    limit,
    sortBy,
    searchValue,
    filters,
  });

  const { data: cabinTotals } = useQueryCabinIssueTotals({ aircraft_id: id });

  const handleAddNewIssue = (): void => {
    dispatch(changeDrawerVisibility({ payload: true }));
    dispatch(changeDrawerContent({ payload: { content: <CabinIssueDrawer issue={null} /> } }));
    dispatch(changeDrawerMode({ payload: DrawerMode.ADD }));
  };

  const onSortChange = (input): void => {
    setSortBy(input);
  };

  const resetFilters = (): void => {
    setReset(true);
    setSortBy([]);
    setSearchValue('');
    setPage(1);
  };

  const updateCabinIssueArray = (array): void => {
    setCabinIssues(array);
    setReset(false);
  };

  const locationToggle = (): ReactElement => {
    return (
      <LocationWrapper>
        <RadioWrapper>
          <RadioInputButton
            text="Location"
            active={location === 'location'}
            checkbox
            marginRight="8px"
            onClick={(): void => setLocation('location')}
          />
          <RadioInputButton
            text="Custom"
            active={location === 'custom'}
            checkbox
            marginRight="8px"
            onClick={(): void => setLocation('custom')}
          />
        </RadioWrapper>
        <LocationText>
          {location === 'location' ? 'Click on a location in the drawing' : 'Click anywhere on the image below'}
        </LocationText>
      </LocationWrapper>
    );
  };

  const handlePointClick = (item): void => {
    dispatch(changeDrawerVisibility({ payload: true }));
    dispatch(setDrawerId({ payload: item.associated_record_id }));
    dispatch(changeDrawerContent({ payload: { content: <CabinIssueDrawer issue={item} /> } }));
  };

  const findCabinIssue = (locationName: string) => {
    // Find the location definition to get its UUID from the element identifier
    const locationId = cabinIssueSVG?.aircraft_location_groups
      ?.flatMap(group => group?.aircraft_locations)
      ?.find(loc => loc?.element_identifier === locationName)?.id;

    // Find the last cabin issue because this is what appears on the SVG when multiple issues share the same location
    return cabinIssuesRef.current?.slice().reverse().find(issue =>
      issue.aircraft_location_issues?.some(loc =>
        loc.aircraft_location_id === locationId
      )
    );
  };

  useEffect(() => {
    const hasSearchChanged = !_.isEqual(searchValue, previousSearchValue);
    const hasFilterChanged = !_.isEqual(filters, previousFilters);

    if (hasSearchChanged || hasFilterChanged) {
      setPage(1);
    }
  }, [previousSearchValue, searchValue, previousFilters, filters]);

  const debouncedSearch = _.debounce((value: string) => setSearchValue(value), 500);

  useEffect(() => {
    if (cabinData) {
      setCabinIssues([...cabinData.cabin_issues]);
      setCabinIssuesOriginal([...cabinData.cabin_issues]);
      setCabinIssuesCount(cabinData.count);
    }
  }, [cabinData]);

  useEffect(() => {
    cabinIssuesRef.current = cabinIssues;
  }, [cabinIssues]);

  useEffect(() => {
    dispatch(getSingleAircraft({ payload: id }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (cabinTotals) {
      const newFilterModel = cabinIssueFilterModel(cabinTotals);
      setFilterModel(newFilterModel);
    }
  }, [cabinTotals]);

  useEffect(() => {
    if (id) {
      setsvgContent(null);
      const svgId = aircraftMap?.get(id)?.active_aircraft_drawing?.id;
      if (svgId) {
        dispatch(getCabinSVG({ payload: { aircraft_id: id, id: svgId } }));
      } else {
        dispatch({
          type: CabinIssuesActionTypes.GET_CABIN_ISSUE_SVG,
          payload: null,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, aircraftMap]);

  useEffect(() => {
    // Only process the SVG if it matches current aircraft
    if (!cabinIssueSVG || cabinIssueSVG.aircraft_id !== id) {
      setsvgContent(null);
      return undefined;
    }

    // function to handle seat click
    const handleMessage = (event: MessageEvent) => {
      // Only process messages from our SVG iframe - Edge seems to be spamming the message event
      if (!iframeRef.current || event.source !== iframeRef.current.contentWindow) {
        return;
      }

      if (event.data && locationRef.current === 'location') {
        const seatState = event.data?.current?.split('-')[2];
        const isAddMode = seatState === 'normal';
        const seatData = {
          id: event.data?.id,
          state: seatState,
        };

        const foundIssue = !isAddMode && findCabinIssue(event.data?.id);
        const locationIssue = foundIssue || seatData;

        dispatch(setSelectedCabinIssueLocation({ payload: seatData }));
        dispatch(changeDrawerVisibility({ payload: true }));
        dispatch(changeDrawerMode({ payload: isAddMode ? DrawerMode.ADD : DrawerMode.VIEW }));
        dispatch(changeDrawerContent({ payload: { content: <CabinIssueDrawer issue={locationIssue} /> } }));
      }
    };

    window.addEventListener('message', handleMessage);

    const fetchSVG = async () => {
      try {
        if (!cabinIssueSVG?.attachment?.id) {
          return;
        }

        const newRes = await fetchImage(cabinIssueSVG?.attachment?.id);
        const parser = new DOMParser();

        const doc = parser.parseFromString(await newRes.text(), 'image/svg+xml');
        const scriptElement = doc.querySelector('script');

        if (scriptElement) {
          const selectLocationFunction = `
            function selectLocation(id, symbolType) {
              const current = document.getElementById(id)?.getAttribute("href");
              const currentStyle = current?.split('-')[2];
              window.parent.postMessage({id, current});
            }

            function alterLocation(id, styleState) {
              const element = document.getElementById(id);

              if (element) {
                const currentHref = element.getAttribute("href");
                const [prefix, symbolType] = currentHref.split("-");

                // desired style to look like "#icon-seata_g650er_jetconcierge-amber"
                element.setAttribute("href", \`\${prefix}-\${symbolType}-\${styleState}\`);
              }
            }
          `;

          const existingScript = scriptElement.textContent;
          const newScript = `${existingScript}\n${selectLocationFunction}`;
          scriptElement.textContent = newScript;
        }

        setsvgContent(doc.documentElement.outerHTML);
      } catch (error) {
        console.error(error);
      }
    };
    fetchSVG();

    return (): void => {
      window.removeEventListener('message', handleMessage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cabinIssueSVG, id]);

  useEffect(() => {
    const handleResize = _.debounce(() => {
      const isNewCompactView = isCompactViewport();
      if (isNewCompactView !== isCompactView) {
        setIsCompactView(isNewCompactView);
      }
    }, 200);

    window.addEventListener('resize', handleResize);
    return () => {
      handleResize.cancel();
      window.removeEventListener('resize', handleResize);
    };
  }, [isCompactView]);

  useEffect(() => {
    if (!cabinIssues || !svgContent) return undefined;

    const openIssues = cabinIssues.filter(
      (issue) => issue.status === 'open' && issue?.aircraft_location_issues?.length > 0,
    );
    const issuesToBeStyled = openIssues?.filter((issue) =>
      issue?.aircraft_location_issues?.some((item) => item?.issue_type_id),
    );
    // function to handle custom point click
    const handleClick = (event, content, element): void => {
        const loc = locationRef.current;
        if (loc === 'location') return;
        const { scrollX, scrollY } = content.defaultView;
        const { width, height } = element.getBoundingClientRect();
        const x = (event.clientX + scrollX) / width;
        const y = (event.clientY + scrollY) / height;
        dispatch(setSelectedCabinIssueLocation({ payload: { x, y } }));
        dispatch(changeDrawerVisibility({ payload: true }));
        dispatch(changeDrawerMode({ payload: DrawerMode.ADD }));
        dispatch(changeDrawerContent({ payload: { content: <CabinIssueDrawer issue={{ x, y }} /> } }));
      };

      let iframe = document.getElementById('cabinlayout') as HTMLIFrameElement;

      if (isCompactView) iframe = document.getElementById('cabinlayoutmobile') as HTMLIFrameElement;
      iframeRef.current = iframe;
      const handleIframeUpdates = () => {
        const iframeWindow = iframe?.contentWindow as CustomWindow | null;
        if (iframeWindow && iframeWindow.alterLocation) {
          // reset svg styling
          const allLocations = cabinIssueSVG?.aircraft_location_groups?.flatMap((outer) => outer?.aircraft_locations);
          if (allLocations) {
            allLocations.forEach((loc) => {
              iframeWindow.alterLocation(loc?.element_identifier, 'normal');
            });
          }
          const newIssuePoints = [];
          // add current styling
          const aircraftIssueTypes = aircraftMap.get(id)?.issue_types || [];
          issuesToBeStyled.forEach((issue) => {
            issue.aircraft_location_issues
              .filter((item) => item?.issue_type_id)
              .forEach((item) => {
                if (item?.aircraft_location_id) {
                  iframeWindow.alterLocation(
                    cabinIssueSVG?.aircraft_location_groups
                      ?.flatMap((outer) => outer?.aircraft_locations)
                      ?.find((inner) => inner.id === item?.aircraft_location_id)?.element_identifier,
                    aircraftIssueTypes.find((type) => type.id === item?.issue_type_id)?.status,
                  );
                } else if (item?.position_x) {
                  const svgElement = iframe?.contentDocument?.querySelector('svg');
                  const svgWidth = parseFloat(svgElement.getAttribute('width'));
                  const svgHeight = parseFloat(svgElement.getAttribute('height'));
                  const pointStatus = aircraftIssueTypes?.find((iss) => iss.id === item?.issue_type_id)?.status;

                  // -15 on each to make it the center of the 30 x 30 dot.
                  const iframeDocElement = iframe?.contentDocument?.documentElement;
                  const adjustedX = svgWidth * item.position_x - iframeDocElement?.scrollLeft - 15;
                  const adjustedY = svgHeight * item.position_y - iframeDocElement?.scrollTop - 15;

                  const foundIssue = cabinIssues.find((localIssue) => localIssue.id === item.associated_record_id);
                  newIssuePoints.push(
                    <StyledButton key={item?.id} onClick={(): void => handlePointClick(foundIssue)}>
                      <img
                        src={pointStatus === 'red' ? redPin : amberPin}
                        alt="point"
                        style={{
                          position: 'absolute',
                          left: `${adjustedX}px`,
                          top: `${adjustedY}px`,
                          width: '30px',
                          height: '30px',
                        }}
                        id={item?.id}
                      />
                    </StyledButton>,
                  );
                }
              });
          });
          setIssuePoints(newIssuePoints);
        }
      };

      let observer = null;
      let element = null;
      let iframeContent = null;
      let clickHandler = null;

      const observeIframeContent = () => {
        observer = new MutationObserver(handleIframeUpdates);
        const config = { childList: true, subtree: true };
        observer.observe(iframeRef.current.contentDocument.documentElement, config);
      };

      locationRef.current = location;

      const handleScrollEvent = () => handleIframeUpdates();

      const handleLoadEvent = () => {
        handleIframeUpdates();
        observeIframeContent();
        // listen for custom clicks
        iframeContent = iframe.contentDocument;
        element = iframeContent.querySelector('svg');
        const bodyElement = iframeContent.querySelector('body');
        if (bodyElement) bodyElement.style.margin = '0';
        if (element) {
          setIframeWidth(parseFloat(element.getAttribute('width')) + 4);
          setIframeHeight(parseFloat(element.getAttribute('height')) + 4);
          setClipWidth(iframeContent.documentElement.clientWidth);
          setClipHeight(iframeContent.documentElement.clientHeight);

          clickHandler = (e) => handleClick(e, iframeContent, element);

          element.addEventListener('click', clickHandler);
          iframeContent.addEventListener('scroll', handleScrollEvent);
        }
      };

      if (iframeRef.current) {
        iframeRef.current.addEventListener('load', handleLoadEvent);
        // also needs to be added outside of the load to handle application when resizing.
        iframe.contentDocument.addEventListener('scroll', handleScrollEvent);

        // If iframe already loaded (when switching views), set up listeners now
        if (iframe.contentDocument?.readyState === 'complete') {
          handleLoadEvent();
        }
      }
      handleIframeUpdates();

      return () => {
        if (iframeRef.current) iframeRef.current.removeEventListener('load', handleLoadEvent);
        if (element && clickHandler) element.removeEventListener('click', clickHandler);
        if (iframe?.contentDocument) iframe.contentDocument.removeEventListener('scroll', handleScrollEvent);
        if (observer) observer.disconnect();
      };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cabinIssues, svgContent, location, cabinIssueSVG, isCompactView, id]);

  const buildPoints = (): ReactElement[] => {
    return issuePoints.map((point) => point);
  };

  const renderCabinIframe = (iframeId: string, width: number | string) => (
    <IframeWrapper>
      <iframe
        id={iframeId}
        title="Cabin Layout"
        srcDoc={svgContent}
        width={width}
        height={iframeHeight || 1000}
      />
      <PinOverlay clipWidth={clipWidth} clipHeight={clipHeight}>
        {buildPoints()}
      </PinOverlay>
    </IframeWrapper>
  );

  let subText = 'Try adjusting your filters or searching with another term.' as string | ReactElement;
  let buttonText = 'Clear all';
  if (cabinIssues.length === 0) {
    subText = `You can add your first ${operatorSettings?.cabin_issue_name_singular} now.`;
    buttonText = `Add ${operatorSettings?.cabin_issue_name_singular}`;
  }
  if (!cabinIssueSVG) {
    const supportHelpText = supportEmail ? (
      <>
        <span> {formatMessage({ id: 'text.cabinLogSvgMissingHelpText' })} </span>
        <a href={`mailto:${supportEmail}`}>{supportEmail}</a>
      </>
    ) : null;

    subText = (
      <>
        <span>{formatMessage({ id: 'text.cabinLogSvgMissing' })}</span>
        {supportHelpText}
      </>
    ) as ReactElement;
    buttonText = null;
  }

  return (
    <InnerMenuLayoutNew>
      <>
        <TFLoading loading={isFetching} />
        <SVGContentWrapper>
          <Card>
            <TableWrapper>
              <PageHeader>
                <PageTitle>{cabinIssueOverride || 'Cabin issue'}</PageTitle>
                <TableActions>
                  <SearchWrapper data-testid="CabinLog--SearchWrapper">
                    <Search
                      onChange={(e): void => debouncedSearch(e.target.value)}
                      onClear={(): void => debouncedSearch('')}
                      reset={reset}
                    />
                  </SearchWrapper>
                  {cabinIssueSVG ? (
                    <Button size={ButtonSize.MEDIUM} onClick={handleAddNewIssue}>
                      <div>
                        <img src={plusIcon} alt="plus icon" />{' '}
                        {mobileView
                          ? formatMessage({ id: 'form.button.add' })
                          : formatMessage({ id: 'text.addCabinIssue' })}
                      </div>
                    </Button>
                  ) : null}
                </TableActions>
              </PageHeader>
              <CabinLogTable
                data={cabinIssues}
                onPaginationChange={(currentPage, numberOfItems): void => {
                  if (numberOfItems !== limit) {
                    setPage(1);
                    setLimit(numberOfItems);
                  } else if (currentPage !== page) {
                    setPage(currentPage);
                  }
                }}
                onSortChange={(sort): void => onSortChange(sort)}
                total={cabinIssuesCount}
                pageSize={limit}
                pageIndex={page - 1}
              />
              <EmptyState
                visible={!cabinIssueSVG || !cabinIssues?.length}
                image={EmptyStateIntermittent}
                text={
                  cabinIssuesOriginal.length === 0
                    ? `No ${operatorSettings?.cabin_issue_name_plural}`
                    : `We couldn't find any matching ${operatorSettings?.cabin_issue_name_plural}`
                }
                subText={subText}
                button={buttonText}
                buttonAction={cabinIssuesOriginal.length === 0 ? handleAddNewIssue : resetFilters}
                subTextWidth="400px"
              />
            </TableWrapper>
            <FilterSideBar
              data={cabinIssuesOriginal}
              updateArray={updateCabinIssueArray}
              filterGroups={filterModel}
              reset={reset}
              onChange={(e): void => {
                if (e.length === 0) {
                  setReset(false);
                  setFilters([]);
                } else {
                  const newFilters = e.reduce((newObject, object) => {
                    const workingObject = newObject;
                    if (!workingObject[object.key]) {
                      workingObject[object.key] = [object.value];
                    } else {
                      workingObject[object.key].push(object.value);
                    }
                    return workingObject;
                  }, {});
                  setFilters(newFilters);
                  setReset(false);
                }
              }}
            />
          </Card>
          {svgContent ? (
            <SVGContent>
              {locationToggle()}
              {renderCabinIframe('cabinlayout', iframeWidth || 400)}
            </SVGContent>
          ) : null}
        </SVGContentWrapper>
        {svgContent ? (
          <SVGContentMobile>
            {locationToggle()}
            {renderCabinIframe('cabinlayoutmobile', iframeWidth || '100%')}
          </SVGContentMobile>
        ) : null}
      </>
    </InnerMenuLayoutNew>
  );
};

export default CabinLog;
